/**
 * An ENUM of available permissions.
 * @num { Number }
 * School Permissions area
 * Student Permissions area
 * Teacher Permissions area
 * Subject Permissions area
 * StudentGroup Permissions area
 * PreferenceGroup Permissions area
 * StudentSet Permissions area
 * Timetable Permissions area
 * Room Permissions area
 * Day Permissions area
 * Line Permissions area
 * Period Permissions area
 * Class Permissions area
 * Preference Permissions area
 * ImportFiles Permissions area
 * Users Permissions area
 * Permissions Permissions area
 * Contacts Permissions area
 * DailyOrganiser Permissions area
 * ScheduledTasks Permissions area
 * Integration Permissions area
 * EmailSettings Permissions area
 * Email Permissions area
 * SchoolAccess Permissions area
 * Email Permissions area
 * ExportFiles Permissions area
 * Support Permissions area
 * SystemLogs Permissions area
 * Organisation Permissions area
 * Faculty Permissions area
 */
export enum PermissionsType {
    Unknown = 0,
    School = 1,
    Student = 2,
    Teacher = 3,
    Subject = 4,
    Timetable = 8,
    Room = 9,
    Day = 10,
    Line = 11,
    Period = 12,
    Class = 13,
    ImportFiles = 15,
    Users = 16,
    Permissions = 17,
    Contacts = 18,
    DailyOrganiser = 19,
    ScheduledTasks = 20,
    Integration = 21,
    EmailSettings = 22,
    Messaging = 23,
    SchoolAccess = 24,
    ExportFiles = 25,
    Support = 26,
    SystemLogs = 27,
    Organisation = 28,
    Faculty = 29,
    PreferencesManager = 30,
    CourseManager = 31,
    Applications = 32,
    Staffing = 33,
    YearLevel = 34,
}

export enum RestrictedPermissions {
    Unknown = 0,
    Subject = 4,
    StudentGroup = 5,
    PreferenceGroup = 6,
    StudentSet = 7,
    Room = 9,
    Day = 10,
    Line = 11,
    Period = 12,
    Class = 13,
    Preference = 14,
    ScheduledTasks = 20,
    SchoolAccess = 24,
    Support = 26,
    SystemLogs = 27,
    Faculty = 29,
    Applications = 32,
}

/**
 * An ENUM of permission access levels.
 * @enum { Number }
 * NoAccess The user has no access
 * ReadAccess The user has read access only
 * WriteAccess The user has write access only
 * ReadWriteAccess The user has read and write access
 */
export enum PermissionsLevel {
    NoAccess = 1,
    ReadAccess = 2,
    WriteAccess = 3,
    ReadWriteAccess = 4,
}

/**
 * This is an ENUM of access scopes.
 * @property {String} Single The user has only access to their own data
 * @property {String} Multi The user has access to their own data and those related to them
 * @property {String} Admin The user has access to all data
 * @property {String} None The user has no access to any data
 */
export enum PermissionsScope {
    Single = "S",
    Multi = "M",
    Admin = "A",
    None = "N",
}

class PermissionSetPermissions {
    Applications: boolean;
    Class: boolean;
    Contacts: boolean;
    CourseManager: boolean;
    DailyOrganiser: boolean;
    Day: boolean;
    EmailSettings: boolean;
    ExportFiles: boolean;
    Faculty: boolean;
    ImportFiles: boolean;
    Integration: boolean;
    Line: boolean;
    Messaging: boolean;
    Organisation: boolean;
    Period: boolean;
    Permissions: boolean;
    PreferencesManager: boolean;
    Room: boolean;
    ScheduledTasks: boolean;
    School: boolean;
    SchoolAccess: boolean;
    Student: boolean;
    Subject: boolean;
    Support: boolean;
    SystemLogs: boolean;
    Teacher: boolean;
    Timetable: boolean;
    Unknown: boolean;
    Users: boolean;
}

interface PermissionInfo {
    readAdminPermissions: bigint;
    readMultiPermissions: bigint;
    readSinglePermissions: bigint;
    writeAdminPermissions: bigint;
    writeMultiPermissions: bigint;
    writeSinglePermissions: bigint;
}

class PermissionSet {
    isStudent: boolean;
    isSupport: boolean;
    isSystemAdmin: boolean;
    private readAdminPermissions: PermissionSetPermissions;
    private readMultiPermissions: PermissionSetPermissions;
    private readSinglePermissions: PermissionSetPermissions;
    private writeAdminPermissions: PermissionSetPermissions;
    private writeMultiPermissions: PermissionSetPermissions;
    private writeSinglePermissions: PermissionSetPermissions;

    private permissions: Record<string, PermissionSetPermissions> = {};

    constructor(data?: Partial<PermissionSet>) {
        if (!!data) Object.assign(this, data);
    }

    init(data: PermissionSet) {
        this.isStudent = data.isStudent ?? false;
        this.isSupport = data.isSupport ?? false;
        this.isSystemAdmin = data.isSystemAdmin ?? false;
        this.readAdminPermissions = data.readAdminPermissions;
        this.readMultiPermissions = data.readMultiPermissions;
        this.readSinglePermissions = data.readSinglePermissions;
        this.writeAdminPermissions = data.writeAdminPermissions;
        this.writeMultiPermissions = data.writeMultiPermissions;
        this.writeSinglePermissions = data.writeSinglePermissions;
        this.permissions["RS"] = data.readSinglePermissions;
        this.permissions["RM"] = data.readMultiPermissions;
        this.permissions["RA"] = data.readAdminPermissions;
        this.permissions["WS"] = data.writeSinglePermissions;
        this.permissions["WM"] = data.writeMultiPermissions;
        this.permissions["WA"] = data.writeAdminPermissions;
    }

    parse(data: PermissionInfo) {
        this.isStudent = false;
        this.isSupport = false;
        this.isSystemAdmin = false;
        this.readAdminPermissions = this.parsePermission(
            data.readAdminPermissions,
        );
        this.readMultiPermissions = this.parsePermission(
            data.readMultiPermissions,
        );
        this.readSinglePermissions = this.parsePermission(
            data.readSinglePermissions,
        );
        this.writeAdminPermissions = this.parsePermission(
            data.writeAdminPermissions,
        );
        this.writeMultiPermissions = this.parsePermission(
            data.writeMultiPermissions,
        );
        this.writeSinglePermissions = this.parsePermission(
            data.writeSinglePermissions,
        );
        this.permissions["RS"] = this.readSinglePermissions;
        this.permissions["RM"] = this.readMultiPermissions;
        this.permissions["RA"] = this.readAdminPermissions;
        this.permissions["WS"] = this.writeSinglePermissions;
        this.permissions["WM"] = this.writeMultiPermissions;
        this.permissions["WA"] = this.writeAdminPermissions;
    }

    private parsePermission(permission: bigint): PermissionSetPermissions {
        permission = BigInt(permission);
        const result = new PermissionSetPermissions();
        for (const key in PermissionsType) {
            if (isNaN(Number(key))) {
                const permissionType =
                    PermissionsType[key as keyof typeof PermissionsType];
                const mask = 1n << BigInt(permissionType);
                const hasPermission = (permission & mask) === mask;
                result[key] = hasPermission;
            }
        }
        return result;
    }

    private __setPermission(
        type: PermissionsType,
        scope: PermissionsScope,
        level: PermissionsLevel,
    ) {
        if (!this.permissions["R" + scope])
            this.permissions["R" + scope] = new PermissionSetPermissions();
        this.permissions["R" + scope][PermissionsType[type]] =
            level === PermissionsLevel.ReadAccess ||
            level === PermissionsLevel.ReadWriteAccess;

        if (!this.permissions["W" + scope])
            this.permissions["W" + scope] = new PermissionSetPermissions();
        this.permissions["W" + scope][PermissionsType[type]] =
            level === PermissionsLevel.WriteAccess ||
            level === PermissionsLevel.ReadWriteAccess;
    }

    private __findPermission(collection, type) {
        return this.permissions[collection][PermissionsType[type]];
    }

    /**
     * Returns the scope of the access the user has to the
     * given permission type for reading.
     * @returns {PermissionsScope} The scope of access the user
     * @param {permissionsType} type The permission to test
     */
    getPermissionScopeRead(type) {
        if (this.__findPermission("RA", type)) return PermissionsScope.Admin;
        if (this.__findPermission("RM", type)) return PermissionsScope.Multi;
        if (this.__findPermission("RS", type)) return PermissionsScope.Single;
        else return PermissionsScope.None;
    }

    /**
     * Returns the scope of the access the user has to the
     * given permission type for writing.
     * @returns {PermissionsScope} The scope of access the user
     * @param {permissionsType} type The permission to test
     */
    getPermissionScopeWrite(type) {
        if (this.__findPermission("WA", type)) return PermissionsScope.Admin;
        if (this.__findPermission("WM", type)) return PermissionsScope.Multi;
        if (this.__findPermission("WS", type)) return PermissionsScope.Single;
        else return PermissionsScope.None;
    }

    /**
     * Returns the level of access the user has to the given
     * permission type.
     * @returns {PermissionsLevel} The level of access the user
     * has to the given permission
     * @param {permissionsType} type The permission to test
     */
    getPermission(type) {
        // Test if any read permissions are set
        let hasAnyRead =
            this.getPermissionScopeRead(type) !== PermissionsScope.None;
        // Test if any write permissions are set
        let hasAnyWrite =
            this.getPermissionScopeWrite(type) !== PermissionsScope.None;

        if (hasAnyRead && hasAnyWrite) return PermissionsLevel.ReadWriteAccess;
        else if (hasAnyWrite) return PermissionsLevel.WriteAccess;
        else if (hasAnyRead) return PermissionsLevel.ReadAccess;
        else return PermissionsLevel.NoAccess;
    }

    /**
     * Returns the level of access the user has to the given
     * permission type.
     * @returns {PermissionsLevel} The level of access the user
     * has to the given permission
     * @param {permissionsType} type The permission to test
     */
    getSinglePermission(type) {
        // Test if any read permissions are set
        let hasAnyRead =
            this.getPermissionScopeRead(type) === PermissionsScope.Single;
        // Test if any write permissions are set
        let hasAnyWrite =
            this.getPermissionScopeWrite(type) === PermissionsScope.Single;

        if (hasAnyRead && hasAnyWrite) return PermissionsLevel.ReadWriteAccess;
        else if (hasAnyWrite) return PermissionsLevel.WriteAccess;
        else if (hasAnyRead) return PermissionsLevel.ReadAccess;
        else return PermissionsLevel.NoAccess;
    }

    /**
     * Returns the level of access the user has to the given
     * permission type.
     * @returns {PermissionsLevel} The level of access the user
     * has to the given permission
     * @param {permissionsType} type The permission to test
     */
    getMultiPermission(type) {
        // Test if any read permissions are set
        let hasAnyRead =
            this.getPermissionScopeRead(type) === PermissionsScope.Multi;
        // Test if any write permissions are set
        let hasAnyWrite =
            this.getPermissionScopeWrite(type) === PermissionsScope.Multi;

        if (hasAnyRead && hasAnyWrite) return PermissionsLevel.ReadWriteAccess;
        else if (hasAnyWrite) return PermissionsLevel.WriteAccess;
        else if (hasAnyRead) return PermissionsLevel.ReadAccess;
        else return PermissionsLevel.NoAccess;
    }

    /**
     * Returns the level of access the user has to the given
     * permission type.
     * @returns {PermissionsLevel} The level of access the user
     * has to the given permission
     * @param {permissionsType} type The permission to test
     */
    getAdminPermission(type) {
        // Test if any read permissions are set
        let hasAnyRead =
            this.getPermissionScopeRead(type) === PermissionsScope.Admin;
        // Test if any write permissions are set
        let hasAnyWrite =
            this.getPermissionScopeWrite(type) === PermissionsScope.Admin;

        if (hasAnyRead && hasAnyWrite) return PermissionsLevel.ReadWriteAccess;
        else if (hasAnyWrite) return PermissionsLevel.WriteAccess;
        else if (hasAnyRead) return PermissionsLevel.ReadAccess;
        else return PermissionsLevel.NoAccess;
    }

    /**
     * Returns whether the given permission has read access.
     * @returns {Boolean} Whether the permission has read access
     * @param {permissionsType} type The permission to test
     */
    canRead(type) {
        return [
            PermissionsLevel.ReadAccess,
            PermissionsLevel.ReadWriteAccess,
        ].includes(this.getPermission(type));
    }

    /**
     * Returns whether the given permission has read access.
     * @returns {Boolean} Whether the permission has read access
     * @param {permissionsType} type The permission to test
     */
    canSingleRead(type) {
        return (
            [
                PermissionsLevel.ReadAccess,
                PermissionsLevel.ReadWriteAccess,
            ].includes(this.getSinglePermission(type)) ||
            [
                PermissionsLevel.ReadAccess,
                PermissionsLevel.ReadWriteAccess,
            ].includes(this.getMultiPermission(type)) ||
            [
                PermissionsLevel.ReadAccess,
                PermissionsLevel.ReadWriteAccess,
            ].includes(this.getAdminPermission(type))
        );
    }

    /**
     * Returns whether the given permission has read access.
     * @returns {Boolean} Whether the permission has read access
     * @param {permissionsType} type The permission to test
     */
    canMultiRead(type) {
        return (
            [
                PermissionsLevel.ReadAccess,
                PermissionsLevel.ReadWriteAccess,
            ].includes(this.getMultiPermission(type)) ||
            [
                PermissionsLevel.ReadAccess,
                PermissionsLevel.ReadWriteAccess,
            ].includes(this.getAdminPermission(type))
        );
    }

    /**
     * Returns whether the given permission has read access.
     * @returns {Boolean} Whether the permission has read access
     * @param {permissionsType} type The permission to test
     */
    canAdminRead(type) {
        return [
            PermissionsLevel.ReadAccess,
            PermissionsLevel.ReadWriteAccess,
        ].includes(this.getAdminPermission(type));
    }

    /**
     * Returns whether the given permission has write access.
     * @returns {Boolean} Whether the permission has write access
     * @param {permissionsType} type The permission to test
     */
    canSingleWrite(type) {
        return (
            [
                PermissionsLevel.WriteAccess,
                PermissionsLevel.ReadWriteAccess,
            ].includes(this.getSinglePermission(type)) ||
            [
                PermissionsLevel.WriteAccess,
                PermissionsLevel.ReadWriteAccess,
            ].includes(this.getMultiPermission(type)) ||
            [
                PermissionsLevel.WriteAccess,
                PermissionsLevel.ReadWriteAccess,
            ].includes(this.getAdminPermission(type))
        );
    }

    /**
     * Returns whether the given permission has write access.
     * @returns {Boolean} Whether the permission has write access
     * @param {permissionsType} type The permission to test
     */
    canMultiWrite(type) {
        return (
            [
                PermissionsLevel.WriteAccess,
                PermissionsLevel.ReadWriteAccess,
            ].includes(this.getMultiPermission(type)) ||
            [
                PermissionsLevel.WriteAccess,
                PermissionsLevel.ReadWriteAccess,
            ].includes(this.getAdminPermission(type))
        );
    }

    /**
     * Returns whether the given permission has write access.
     * @returns {Boolean} Whether the permission has write access
     * @param {permissionsType} type The permission to test
     */
    canAdminWrite(type) {
        return [
            PermissionsLevel.WriteAccess,
            PermissionsLevel.ReadWriteAccess,
        ].includes(this.getAdminPermission(type));
    }

    /**
     * Returns whether the given permission has write access.
     * @returns {Boolean} Whether the permission has write access
     * @param {permissionsType} type The permission to test
     */
    canWrite(type) {
        return [
            PermissionsLevel.WriteAccess,
            PermissionsLevel.ReadWriteAccess,
        ].includes(this.getPermission(type));
    }

    resetPermissions() {
        for (const key in PermissionsType) {
            if (isNaN(Number(key))) {
                for (const scope in PermissionsScope) {
                    if (isNaN(Number(scope))) {
                        this.__setPermission(
                            PermissionsType[
                                key as keyof typeof PermissionsType
                            ],
                            PermissionsScope[
                                scope as keyof typeof PermissionsScope
                            ],
                            PermissionsLevel.NoAccess,
                        );
                    }
                }
            }
        }
    }

    /**
     * Sets the given permission type for the given scope to
     * the given read and write access levels.
     * @param {PermissionsType} type The permission to set
     * @param {PermissionsScope} scope The scope to set it for
     * @param {Boolean} read If the user can read
     * @param {Boolean} write If the user can write
     */
    setPermissions(
        type: PermissionsType,
        scope: PermissionsScope,
        read: boolean,
        write: boolean,
    ) {
        var temp = PermissionsLevel.NoAccess;
        if (write) {
            if (read) temp = PermissionsLevel.ReadWriteAccess;
            else temp = PermissionsLevel.WriteAccess;
        } else if (read) temp = PermissionsLevel.ReadAccess;
        this.__setPermission(type, scope, temp);
    }

    /**
     * Returns the raw permissions object for the given scope
     * and level.
     * @returns {Object} Raw server permissions object
     * @param {PermissionsScope} scope The scope to find
     * @param {PermissionsLevel} level The level to find (read or write only)
     */
    getRawPermission(scope: PermissionsScope, level: PermissionsLevel) {
        const entry =
            this.permissions[
                (level === PermissionsLevel.ReadAccess
                    ? "R"
                    : level === PermissionsLevel.WriteAccess
                    ? "W"
                    : "") + scope
            ];

        let result = 0n;
        for (const key in PermissionsType) {
            if (
                isNaN(Number(key)) &&
                entry[key as keyof typeof PermissionsType]
            ) {
                const permissionType =
                    PermissionsType[key as keyof typeof PermissionsType];
                const mask = 1n << BigInt(permissionType);
                result = result | mask;
            }
        }
        return result;
    }

    /**
     * Returns a valid permissions object containing the current
     * values.
     * @returns {Object} A valid server compatible permissions
     * configuraiton object
     * */
    exportPermissions() {
        return {
            readSinglePermissions: this.getRawPermission(
                PermissionsScope.Single,
                PermissionsLevel.ReadAccess,
            ),
            readMultiPermissions: this.getRawPermission(
                PermissionsScope.Multi,
                PermissionsLevel.ReadAccess,
            ),
            readAdminPermissions: this.getRawPermission(
                PermissionsScope.Admin,
                PermissionsLevel.ReadAccess,
            ),
            writeSinglePermissions: this.getRawPermission(
                PermissionsScope.Single,
                PermissionsLevel.WriteAccess,
            ),
            writeMultiPermissions: this.getRawPermission(
                PermissionsScope.Multi,
                PermissionsLevel.WriteAccess,
            ),
            writeAdminPermissions: this.getRawPermission(
                PermissionsScope.Admin,
                PermissionsLevel.WriteAccess,
            ),
        } as PermissionInfo;
    }
}

export default PermissionSet;
